<section class="customer-packaging-details" aria-labelledby="customer-packaging-details-heading">
  <div class="customer-packaging-details__summary">
    <div class="customer-packaging-details__totals">
      <div class="customer-packaging-details__total-field">
        <div class="customer-packaging-details__total-text">
          <span class="customer-packaging-details__total-label"
            >{{ 'createShipment.packageStep.details.totalPieces' | translate
            }}<span class="customer-packaging-details__required" aria-hidden="true">*</span></span
          >
          <span class="customer-packaging-details__total-value">{{ totalPieces() }}</span>
        </div>
      </div>
      <div class="customer-packaging-details__total-field">
        <div class="customer-packaging-details__total-text">
          <span class="customer-packaging-details__total-label"
            >{{ 'createShipment.packageStep.details.totalWeight' | translate
            }}<span class="customer-packaging-details__required" aria-hidden="true">*</span></span
          >
          <span class="customer-packaging-details__total-value">{{ formattedTotalWeight() }}</span>
        </div>
        <span class="customer-packaging-details__total-unit">{{
          (unitSystem() === 'imperial'
            ? 'createShipment.packageStep.details.weightUnitLb'
            : 'createShipment.packageStep.details.weightUnitKg'
          ) | translate
        }}</span>
      </div>
    </div>

    <div class="customer-packaging-details__unit-row">
      <puro-typography
        id="customer-packaging-details-heading"
        class="customer-packaging-details__heading"
        variant="h5-semibold"
        component="h2"
        color="textPrimary"
      >
        {{ 'createShipment.packageStep.details.enterPackageDetails' | translate }}
      </puro-typography>
      <fieldset class="customer-packaging-details__unit-toggle">
        <legend class="customer-packaging-details__unit-label">
          {{ 'createShipment.packageStep.details.unitOfMeasurement' | translate }}
        </legend>
        <div class="customer-packaging-details__unit-track">
          <button
            type="button"
            class="customer-packaging-details__unit-btn"
            [class.customer-packaging-details__unit-btn--active]="unitSystem() === 'imperial'"
            [attr.aria-pressed]="unitSystem() === 'imperial'"
            (click)="onUnitToggle('imperial')"
          >
            {{ 'createShipment.packageStep.details.unitImperial' | translate }}
          </button>
          <button
            type="button"
            class="customer-packaging-details__unit-btn"
            [class.customer-packaging-details__unit-btn--active]="unitSystem() === 'metric'"
            [attr.aria-pressed]="unitSystem() === 'metric'"
            (click)="onUnitToggle('metric')"
          >
            {{ 'createShipment.packageStep.details.unitMetric' | translate }}
          </button>
        </div>
      </fieldset>
    </div>

    <p class="customer-packaging-details__helper">
      {{ 'createShipment.packageStep.details.helperText' | translate }}
    </p>
  </div>

  @if (showOverFiftyWarning()) {
    <app-alert-toast
      class="customer-packaging-details__info-banner"
      severity="warning"
      live="polite"
      [message]="'createShipment.packageStep.details.overFiftyWarning' | translate"
    />
  }

  @for (type of specialHandlingTypes(); track type) {
    @let shMessage = specialHandlingMessageKey(type) | translate;
    @if (shMessage) {
      <app-alert-toast
        class="customer-packaging-details__info-banner"
        severity="warning"
        live="polite"
        [message]="shMessage"
      />
    }
  }

  @if (showErrorBanner()) {
    <app-alert-toast
      class="customer-packaging-details__error-banner"
      severity="error"
      live="assertive"
    >
      <p class="customer-packaging-details__error-line customer-packaging-details__error-title">
        {{ 'createShipment.packageStep.details.validationSummaryTitle' | translate }}
      </p>
      @for (error of bannerErrors().shipmentErrors; track error.messageKey) {
        <p class="customer-packaging-details__error-line">
          {{ error.messageKey | translate: error.params ?? bannerMessageParams(error.messageKey) }}
        </p>
      }
      @for (group of bannerErrors().packageGroups; track group.packageNumber) {
        <p class="customer-packaging-details__error-line customer-packaging-details__error-package">
          {{
            'createShipment.packageStep.details.packageErrorHeading'
              | translate: { index: '' + group.packageNumber }
          }}
        </p>
        <ul class="customer-packaging-details__error-list">
          @for (msg of group.messages; track msg.messageKey) {
            <li class="customer-packaging-details__error-line">
              @if (msg.emphasisKey && msg.detailKey) {
                <strong class="customer-packaging-details__error-emphasis">{{
                  msg.emphasisKey | translate
                }}</strong
                >{{ msg.detailKey | translate: bannerMessageParams(msg.messageKey) }}
              } @else {
                {{ msg.messageKey | translate: bannerMessageParams(msg.messageKey) }}
              }
            </li>
          }
        </ul>
      }
      @if (bannerErrors().showSurchargeFooter) {
        <p class="customer-packaging-details__error-surcharge">
          {{ 'createShipment.packageStep.details.overMaxSurchargeBeforeLink' | translate }}
          <a
            class="customer-packaging-details__error-link"
            [href]="'createShipment.packageStep.details.overMaxSurchargeUrl' | translate"
            target="_blank"
            rel="noopener noreferrer"
            >{{ 'createShipment.packageStep.details.overMaxSurchargeLink' | translate }}</a
          >{{ 'createShipment.packageStep.details.overMaxSurchargeAfterLink' | translate }}
        </p>
      }
    </app-alert-toast>
  }

  @if (usePackageCards()) {
    <div class="customer-packaging-details__cards">
      @for (row of pieces().controls; track row; let i = $index) {
        <app-customer-packaging-card
          [row]="row"
          [index]="i"
          [pieceCount]="pieceCount()"
          [syncKey]="pieceRowTrackKey(i)"
          [unitSystem]="unitSystem()"
          [showValidationErrors]="showValidationErrors()"
          (clearRow)="clearRow.emit($event)"
          (removeRow)="removeRow.emit($event)"
        />
      }

      @if (canAddAnotherPackage()) {
        <puro-button
          type="button"
          variant="primary"
          size="medium"
          class="customer-packaging-details__add customer-packaging-details__add--full"
          (buttonClick)="addPackage.emit()"
        >
          + {{ 'createShipment.packageStep.details.addAnotherPackage' | translate }}
        </puro-button>
      }
    </div>
  } @else {
    <div class="customer-packaging-details__table-block">
      <div class="customer-packaging-details__table-wrap">
        <table
          class="customer-packaging-details__table"
          [attr.aria-label]="'createShipment.packageStep.details.packagesColumn' | translate"
        >
          <thead>
            <tr class="customer-packaging-details__table-header">
              <th
                scope="col"
                class="customer-packaging-details__th customer-packaging-details__th--packages"
              >
                <span class="customer-packaging-details__label-full">{{
                  'createShipment.packageStep.details.packagesColumn' | translate
                }}</span>
                <span class="customer-packaging-details__label-short">{{
                  'createShipment.packageStep.details.packagesColumnShort' | translate
                }}</span>
              </th>
              <th
                scope="col"
                class="customer-packaging-details__th customer-packaging-details__th--center"
              >
                <span class="customer-packaging-details__th-with-info">
                  <span
                    >{{ 'createShipment.packageStep.details.identicalPieces' | translate
                    }}<span class="customer-packaging-details__required" aria-hidden="true"
                      >*</span
                    ></span
                  >
                  <span class="visually-hidden" id="package-identical-pieces-tooltip-desc">{{
                    'createShipment.packageStep.details.identicalPiecesTooltip' | translate
                  }}</span>
                  <puro-tooltip
                    #identicalPiecesTooltip
                    [content]="
                      'createShipment.packageStep.details.identicalPiecesTooltip' | translate
                    "
                  >
                    <button
                      type="button"
                      class="customer-packaging-details__info-btn"
                      [attr.aria-label]="
                        'createShipment.packageStep.details.identicalPiecesTooltipAria' | translate
                      "
                      aria-describedby="package-identical-pieces-tooltip-desc"
                      (click)="$event.preventDefault(); $event.stopPropagation()"
                      (focus)="identicalPiecesTooltip.show()"
                      (blur)="identicalPiecesTooltip.hide()"
                    >
                      <puro-icon name="info-circle-tooltip" size="sm" aria-hidden="true" />
                    </button>
                  </puro-tooltip>
                </span>
              </th>
              <th
                scope="col"
                class="customer-packaging-details__th customer-packaging-details__th--center"
              >
                <span class="customer-packaging-details__th-stack">
                  <span
                    >{{ 'createShipment.packageStep.details.perPieceWeight' | translate
                    }}<span class="customer-packaging-details__required" aria-hidden="true"
                      >*</span
                    ></span
                  >
                  <span class="customer-packaging-details__unit-chip">{{
                    weightUnitLabelKey() | translate
                  }}</span>
                </span>
              </th>
              <th
                scope="col"
                colspan="3"
                class="customer-packaging-details__th customer-packaging-details__th--dimension"
              >
                <div class="customer-packaging-details__dimension-block">
                  <div class="customer-packaging-details__dimension-title">
                    <span
                      >{{ 'createShipment.packageStep.details.dimension' | translate
                      }}<span class="customer-packaging-details__required" aria-hidden="true"
                        >*</span
                      ></span
                    >
                    <span class="customer-packaging-details__unit-chip">{{
                      dimensionUnitLabelKey() | translate
                    }}</span>
                    <span class="customer-packaging-details__dimension-info">
                      <span class="visually-hidden" id="package-dimension-tooltip-desc">{{
                        'createShipment.packageStep.details.dimensionTooltip' | translate
                      }}</span>
                      <puro-tooltip
                        #dimTooltip
                        [content]="
                          'createShipment.packageStep.details.dimensionTooltip' | translate
                        "
                      >
                        <button
                          type="button"
                          class="customer-packaging-details__info-btn"
                          [attr.aria-label]="
                            'createShipment.packageStep.details.dimensionTooltipAria' | translate
                          "
                          aria-describedby="package-dimension-tooltip-desc"
                          (click)="$event.preventDefault(); $event.stopPropagation()"
                          (focus)="dimTooltip.show()"
                          (blur)="dimTooltip.hide()"
                        >
                          <puro-icon name="info-circle-tooltip" size="sm" aria-hidden="true" />
                        </button>
                      </puro-tooltip>
                    </span>
                  </div>
                  <div class="customer-packaging-details__dimension-subs">
                    <div
                      class="customer-packaging-details__th customer-packaging-details__th--center"
                    >
                      {{ 'createShipment.packageStep.details.length' | translate
                      }}<span class="customer-packaging-details__required" aria-hidden="true"
                        >*</span
                      >
                    </div>
                    <div
                      class="customer-packaging-details__th customer-packaging-details__th--center"
                    >
                      {{ 'createShipment.packageStep.details.width' | translate
                      }}<span class="customer-packaging-details__required" aria-hidden="true"
                        >*</span
                      >
                    </div>
                    <div
                      class="customer-packaging-details__th customer-packaging-details__th--center"
                    >
                      {{ 'createShipment.packageStep.details.height' | translate
                      }}<span class="customer-packaging-details__required" aria-hidden="true"
                        >*</span
                      >
                    </div>
                  </div>
                </div>
              </th>
              <th
                scope="col"
                class="customer-packaging-details__th customer-packaging-details__th--center customer-packaging-details__th--ah"
              >
                <span class="customer-packaging-details__th-with-info">
                  <span class="customer-packaging-details__ah-label">{{
                    'createShipment.packageStep.details.additionalHandling' | translate
                  }}</span>
                  <span class="visually-hidden" id="package-additional-handling-tooltip-desc">{{
                    'createShipment.packageStep.details.additionalHandlingTooltip' | translate
                  }}</span>
                  <puro-tooltip
                    #ahTooltip
                    [content]="
                      'createShipment.packageStep.details.additionalHandlingTooltip' | translate
                    "
                  >
                    <button
                      type="button"
                      class="customer-packaging-details__info-btn"
                      [attr.aria-label]="
                        'createShipment.packageStep.details.additionalHandlingTooltipAria'
                          | translate
                      "
                      aria-describedby="package-additional-handling-tooltip-desc"
                      (click)="$event.preventDefault(); $event.stopPropagation()"
                      (focus)="ahTooltip.show()"
                      (blur)="ahTooltip.hide()"
                    >
                      <puro-icon name="info-circle-tooltip" size="sm" aria-hidden="true" />
                    </button>
                  </puro-tooltip>
                </span>
              </th>
              <th
                scope="col"
                class="customer-packaging-details__th customer-packaging-details__th--center"
              >
                {{ 'createShipment.packageStep.details.actions' | translate }}
              </th>
            </tr>
          </thead>
          <tbody>
            @for (row of pieces().controls; track row; let i = $index) {
              <tr class="customer-packaging-details__table-row" [formGroup]="row">
                <td
                  class="customer-packaging-details__cell customer-packaging-details__package-name"
                >
                  <span
                    class="customer-packaging-details__label-full"
                    tabindex="-1"
                    [attr.data-testid]="'package-piece-title-' + i"
                    [attr.id]="'package-piece-title-' + i"
                    >{{
                      'createShipment.packageStep.details.packageLabel'
                        | translate: { index: '' + (i + 1) }
                    }}</span
                  >
                  <span class="customer-packaging-details__label-short">{{
                    'createShipment.packageStep.details.packageLabelShort'
                      | translate: { index: '' + (i + 1) }
                  }}</span>
                </td>
                <!-- Remount number inputs when Clear bumps view key (puro-input CVA display sync). -->
                @for (syncKey of [pieceRowTrackKey(i)]; track syncKey) {
                  <td
                    class="customer-packaging-details__cell customer-packaging-details__cell--measure"
                  >
                    <puro-input
                      class="customer-packaging-details__measure-input"
                      type="number"
                      formControlName="identicalPieces"
                      [label]="'createShipment.packageStep.details.identicalPieces' | translate"
                      labelPosition="top"
                      [required]="true"
                      [error]="hasError(i, 'identicalPieces')"
                      [errorMessage]="
                        hasError(i, 'identicalPieces')
                          ? (fieldErrorKey(i, 'identicalPieces') | translate)
                          : ''
                      "
                      (focusout)="onFieldBlur(i, 'identicalPieces')"
                    />
                  </td>
                  <td
                    class="customer-packaging-details__cell customer-packaging-details__cell--measure"
                  >
                    <puro-input
                      class="customer-packaging-details__measure-input"
                      type="text"
                      formControlName="perPieceWeight"
                      appPackageMeasureInput
                      [label]="'createShipment.packageStep.details.perPieceWeight' | translate"
                      labelPosition="top"
                      [required]="true"
                      [error]="hasError(i, 'perPieceWeight')"
                      [errorMessage]="
                        hasError(i, 'perPieceWeight')
                          ? (fieldErrorKey(i, 'perPieceWeight') | translate)
                          : ''
                      "
                      (focusout)="onFieldBlur(i, 'perPieceWeight')"
                    />
                  </td>
                  <td
                    class="customer-packaging-details__cell customer-packaging-details__cell--measure"
                  >
                    <puro-input
                      class="customer-packaging-details__measure-input"
                      type="text"
                      formControlName="length"
                      appPackageMeasureInput
                      [label]="'createShipment.packageStep.details.length' | translate"
                      labelPosition="top"
                      [required]="true"
                      [error]="hasError(i, 'length')"
                      [errorMessage]="
                        hasError(i, 'length') ? (fieldErrorKey(i, 'length') | translate) : ''
                      "
                      (focusout)="onFieldBlur(i, 'length')"
                    />
                  </td>
                  <td
                    class="customer-packaging-details__cell customer-packaging-details__cell--measure"
                  >
                    <puro-input
                      class="customer-packaging-details__measure-input"
                      type="text"
                      formControlName="width"
                      appPackageMeasureInput
                      [label]="'createShipment.packageStep.details.width' | translate"
                      labelPosition="top"
                      [required]="true"
                      [error]="hasError(i, 'width')"
                      [errorMessage]="
                        hasError(i, 'width') ? (fieldErrorKey(i, 'width') | translate) : ''
                      "
                      (focusout)="onFieldBlur(i, 'width')"
                    />
                  </td>
                  <td
                    class="customer-packaging-details__cell customer-packaging-details__cell--measure"
                  >
                    <puro-input
                      class="customer-packaging-details__measure-input"
                      type="text"
                      formControlName="height"
                      appPackageMeasureInput
                      [label]="'createShipment.packageStep.details.height' | translate"
                      labelPosition="top"
                      [required]="true"
                      [error]="hasError(i, 'height')"
                      [errorMessage]="
                        hasError(i, 'height') ? (fieldErrorKey(i, 'height') | translate) : ''
                      "
                      (focusout)="onFieldBlur(i, 'height')"
                    />
                  </td>
                }
                <td
                  class="customer-packaging-details__cell customer-packaging-details__checkbox-cell"
                >
                  <puro-checkbox
                    formControlName="requiresAdditionalHandling"
                    [label]="'createShipment.packageStep.details.additionalHandling' | translate"
                  />
                </td>
                <td
                  class="customer-packaging-details__cell customer-packaging-details__actions-cell"
                >
                  <puro-button
                    type="button"
                    variant="link"
                    size="small"
                    [icon]="true"
                    iconPosition="start"
                    (buttonClick)="clearRow.emit(i)"
                  >
                    <puro-icon icon name="arrow-clockwise" size="sm" aria-hidden="true" />
                    <span class="visually-hidden">{{
                      'createShipment.packageStep.details.clearAria'
                        | translate: { index: '' + (i + 1) }
                    }}</span>
                    <span aria-hidden="true">{{
                      'createShipment.packageStep.details.clear' | translate
                    }}</span>
                  </puro-button>
                  @if (pieceCount() > 1) {
                    <puro-button
                      type="button"
                      variant="link"
                      size="small"
                      [icon]="true"
                      iconPosition="start"
                      (buttonClick)="removeRow.emit(i)"
                    >
                      <puro-icon icon name="trash-delete" size="sm" aria-hidden="true" />
                      <span class="visually-hidden">{{
                        'createShipment.packageStep.details.deleteAria'
                          | translate: { index: '' + (i + 1) }
                      }}</span>
                      <span aria-hidden="true">{{
                        'createShipment.packageStep.details.delete' | translate
                      }}</span>
                    </puro-button>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      @if (canAddAnotherPackage()) {
        <puro-button
          type="button"
          variant="primary"
          size="medium"
          class="customer-packaging-details__add"
          (buttonClick)="addPackage.emit()"
        >
          + {{ 'createShipment.packageStep.details.addAnotherPackage' | translate }}
        </puro-button>
      }
    </div>
  }

  <app-alert-toast
    class="customer-packaging-details__warning"
    severity="warning"
    live="polite"
    [message]="'createShipment.packageStep.details.weightWarning' | translate"
  />
</section>
